import { useEffect } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { GetCurrentSessionResponse } from '@ebalance/proto/bffuser/v1/session_service_pb'
import {
  GetCurrentSessionRequestSchema,
  SessionService,
} from '@ebalance/proto/bffuser/v1/session_service_pb'

import { applyServerTime } from '~/models/clock'

/**
 * BFF session RPC (`GetCurrentSession`) の共有 hook。
 *
 * 消費者は i18n 初期化 (`I18nProvider`。表示言語 `display_language` を読む)・
 * 時計の確定待ち (`ServerClockGate`)・テロップバーの組織名表示 (`MainLayout/TickerBar`)・入札の承認者の識別で、
 * connect-query の cache key が同一なので RPC は共有される (= 定義を複数箇所に持つと一部だけ安全側
 * option が抜ける事故になるため、ここに集約する)。
 *
 * レスポンスの `member_id` は、 承認者でない人・申請者本人への注意で承認者と突合する材料に使う
 * (spot AC-BSP-098 は案件が持つ承認申請の時点の写し `stage_approvers`、 時間前 AC-BIN-098 は
 * 承認フロー設定の `ApprovalFlowStage.approver_member_ids`)。消費側は
 * 取得失敗 (`isError`) を「承認者でない」に倒す (fail-closed)
 * こと。 なお業務ワークフローの承認ゲート
 * (`WorkflowCaseDetail/components/ApprovalGateSection.tsx`) は同じ判定を
 * `@enechain/auth-js` の `useAuth().user?.memberId` で行っているが、入札側は
 * spec の判断 (spot-bidding.md「承認/差戻し操作の認可方式」 2026-08-26) に従い
 * **サーバーが同じ eauth セッションから解決した値**をこの RPC 経由で使う。
 *
 * 既定の option:
 * - `staleTime` / `gcTime` = Infinity … 起動時 1 回で足りる値のため、ウィンドウ
 *   再フォーカスや再マウントで再 fetch しない
 * - `retry: false` … 失敗は各消費者のフォールバック (ja / 既定表示名) で吸収する
 * - `throwOnError: false` … **必須**。本番 `QueryClient` の既定 (`throwOnError: true`)
 *   は observer ごとに評価されるため、ここで false にしないと session RPC 1 本の
 *   失敗が render 中に throw され、全画面がエラーになる (補助情報のために画面を
 *   壊さない)
 *
 * レスポンスの `server_time` はここで [[applyServerTime]] に渡し、 app 横断の時計
 * ([[now]]) の offset を更新する。 差を取る基準には effect 実行時の `Date.now()` では
 * なく `dataUpdatedAt` (= レスポンス到着時のブラウザ時計) を使う (`staleTime: Infinity`
 * なので、 キャッシュ済みレスポンスで effect が再実行されると滞留時間ぶん時計が過去へ
 * ずれる)。 この RPC の消費点が増えても offset が古いまま
 * 取り残されないよう、 消費者側ではなくこの hook に置いている。
 * 取り込みは冪等なので複数 observer から呼ばれてよい。 取得前・
 * 取得失敗時は offset が未設定のままで、 [[now]] はブラウザ時計を返す。
 */
export function useCurrentSession(options?: {
  /**
   * 定期再取得の間隔 (ms)。既定 (undefined) は再取得しない。
   * 表示中に組織が切り替わりうる値 (= テロップバーの組織名) を出す消費者だけが指定する。
   * この再取得で `display_language` が変わっても `I18nProvider` は適用しない
   * (最初に得た確定値だけを適用する。AC-SPS-030 N3)。
   */
  refetchInterval?: number
}): {
  data: GetCurrentSessionResponse | undefined
  isPending: boolean
  /**
   * 直近の取得が失敗しているか。
   *
   * TanStack Query は**再取得**が失敗しても直前に成功した `data` を保持したまま
   * `isPending` を false にする。 そのため `data` の有無だけで判断すると、 別組織へ
   * セッションが切り替わった直後に再取得が落ちたとき「旧組織名を出し続ける」 ことに
   * なる。 表示が取り違え防止を担う消費者は `data` ではなくこちらで分岐する。
   */
  isError: boolean
} {
  const { data, dataUpdatedAt, isPending, isError } = useQuery(
    SessionService.method.getCurrentSession,
    create(GetCurrentSessionRequestSchema, {}),
    {
      gcTime: Infinity,
      refetchInterval: options?.refetchInterval,
      retry: false,
      staleTime: Infinity,
      throwOnError: false,
    },
  )

  const serverTime = data?.serverTime
  useEffect(() => {
    applyServerTime(serverTime, dataUpdatedAt)
  }, [serverTime, dataUpdatedAt])

  return { data, isPending, isError }
}
